import { showSuccess, showError } from "@/lib/toastHelpers";
import { useQuery } from "@tanstack/react-query";
import { useApiMutation } from "@/hooks/useApiMutation";
import { queryClient, apiRequest } from "@/lib/queryClient";
import type { StarterIngredient } from "@/lib/types";
import { useMemo } from "react";

interface UseStarterIngredientsOptions {
  onCreateSuccess?: () => void;
  onUpdateSuccess?: () => void;
}

export function useStarterIngredients(options?: UseStarterIngredientsOptions) {
  const { data: starterIngredients, isLoading: ingredientsLoading } = useQuery<StarterIngredient[]>({
    queryKey: ["/api/starter-pack/ingredients"],
  });

  const createIngredientMutation = useApiMutation({
    endpoint: "/api/starter-pack/ingredients",
    method: "POST",
    invalidate: [["/api/starter-pack/ingredients"]],
    successMessage: "Starter ingredient created",
    errorMessage: "Failed to create ingredient",
    onSuccess: options?.onCreateSuccess,
  });

  const updateIngredientMutation = useApiMutation({
    endpoint: (vars: { id: string }) => `/api/starter-pack/ingredients/${vars.id}`,
    method: "PATCH",
    invalidate: [["/api/starter-pack/ingredients"]],
    successMessage: "Starter ingredient updated",
    errorMessage: "Failed to update ingredient",
    onSuccess: options?.onUpdateSuccess,
  });

  const deleteIngredientMutation = useApiMutation({
    endpoint: (vars: { id: string }) => `/api/starter-pack/ingredients/${vars.id}`,
    method: "DELETE",
    invalidate: [["/api/starter-pack/ingredients"]],
    successMessage: "Starter ingredient deleted",
    errorMessage: "Failed to delete ingredient",
  });

  const importIngredientMutation = useApiMutation({
    endpoint: "/api/starter-pack/import/ingredients",
    method: "POST",
    invalidate: [["/api/ingredients"]],
    successMessage: "Ingredient copied to your pantry",
    errorMessage: "Failed to copy ingredient",
  });

  const importAllIngredientsMutation = useApiMutation({
    endpoint: "/api/starter-pack/import/ingredients",
    method: "POST",
    invalidate: [["/api/ingredients"]],
    successMessage: "All ingredients copied to your pantry",
    errorMessage: "Failed to copy ingredients",
  });
  return {
    starterIngredients,
    ingredientsLoading,
    createIngredientMutation,
    updateIngredientMutation,
    deleteIngredientMutation,
    importIngredientMutation,
    importAllIngredientsMutation,
  };
}
